// src/pages/enterprise/EnterpriseAgentAccessTab.js
//
// Per-org agent enablement UI (Track D / T5).
// Lets mgmt admins force-enable or force-disable each agent for one org.
//
// Data flow: T5 (this tab) -> T6 (admin API) -> T1 (source of truth).
//   GET  ${T6_BASE}/api/admin/orgs/:orgId/agents
//   PUT  ${T6_BASE}/api/admin/orgs/:orgId/agents/:agentId   body { enabled, reason }
//
// Relay access card (RELAY-DISABLE-PANEL-CARD-01), one status read per org:
//   GET  ${T6_BASE}/api/admin/relay/orgs/:orgId              -> { status, changedBy, changedAt, reason, history[] }
//   POST ${T6_BASE}/api/admin/relay/requests/:orgId/approve  body { reason }   (Approve, Enable, Re-enable)
//   POST ${T6_BASE}/api/admin/relay/requests/:orgId/disable  body { reason }   (Disable, reason required)
//
// Auth + base URL follow the project idiom (MyVenuesComponent / apiService):
// Firebase ID token in an Authorization: Bearer header, REACT_APP_BACKEND_URL base.
//
// NOTE: unlike the sibling EnterpriseFeesTab (which is still local-mock),
// this tab performs real network calls per the T5 directive.

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Switch,
    Chip,
    Tooltip,
    Skeleton,
    Alert,
    Snackbar,
    Button,
    Link as MuiLink,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Collapse
} from '@mui/material';
import { styled } from '@mui/system';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import { getAuth } from 'firebase/auth';
import axios from 'axios';

// -----------------------------------------------------------------------------
//  CONSTANTS
// -----------------------------------------------------------------------------

// T6 admin API base. Matches the project convention in apiService.js.
// Exported so sibling enterprise tabs (e.g. EnterpriseTravelTab) reuse the same base.
export const T6_BASE = process.env.REACT_APP_BACKEND_URL || 'https://api.mgmt.joinunited.ai';

// -----------------------------------------------------------------------------
//  STYLED
// -----------------------------------------------------------------------------

const AgentCard = styled(Paper)({
    backgroundColor: '#111',
    border: '1px solid rgba(255,255,255,0.08)',
    color: '#fff',
    borderRadius: 14,
    padding: '1rem 1.25rem',
    marginBottom: '1rem'
});

const mintLinkSx = {
    color: '#12c099',
    fontSize: '0.8rem',
    cursor: 'pointer',
    textDecoration: 'none',
    '&:hover': { textDecoration: 'underline' }
};

// -----------------------------------------------------------------------------
//  HELPERS
// -----------------------------------------------------------------------------

// Stable identifier for an agent across the various field names a backend
// might return. The PUT endpoint keys on this value.
const agentKey = (a) => a.agentId ?? a.id ?? a._id;

// Build the status pill descriptor from override + default state.
function statusPill(agent) {
    if (agent.orgOverride === 'enabled') {
        return { label: 'Force enabled for this org', color: '#12c099', bg: 'rgba(18,192,153,0.15)' };
    }
    if (agent.orgOverride === 'disabled') {
        return { label: 'Force disabled for this org', color: '#ff6b6b', bg: 'rgba(255,107,107,0.15)' };
    }
    // orgOverride is null -> falls back to platform default
    return agent.defaultEnabled
        ? { label: 'Default (enabled)', color: '#aaa', bg: 'rgba(255,255,255,0.06)' }
        : { label: 'Default (disabled)', color: '#aaa', bg: 'rgba(255,255,255,0.06)' };
}

// Relay status pill descriptor. Mirrors the mint/amber/red/gray palette the enterprise
// table and statusPill use.
const RELAY_PILL = {
    enabled: { label: 'Enabled', color: '#12c099', bg: 'rgba(18,192,153,0.15)' },
    requested: { label: 'Requested', color: '#f59e0b', bg: 'rgba(245,158,11,0.15)' },
    not_requested: { label: 'Not requested', color: '#aaa', bg: 'rgba(255,255,255,0.06)' },
    disabled: { label: 'Disabled', color: '#ff6b6b', bg: 'rgba(255,107,107,0.15)' },
    denied: { label: 'Denied', color: '#aaa', bg: 'rgba(255,255,255,0.06)' },
};

// T1's request rows say 'pending' where the card says 'requested'. An unknown status
// renders as its raw word on a grey chip with no buttons, never as a guess.
const normalizeRelayStatus = (s) => {
    const v = String(s ?? '').toLowerCase();
    if (v === 'pending') return 'requested';
    return v || 'not_requested';
};
const relayPillFor = (status) =>
    RELAY_PILL[status] || { label: status, color: '#aaa', bg: 'rgba(255,255,255,0.06)' };

// Who/when/why for the current status: the top-level fields, else the newest history row.
const relayActorName = (by) => {
    if (!by) return null;
    if (typeof by === 'string') return by;
    return by.name || by.displayName || by.email || by.uid || by.employeeId || null;
};
const relayDate = (d) => {
    if (!d) return null;
    const t = new Date(d);
    return Number.isNaN(t.getTime()) ? null : t.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};
const relayEntryBy = (e) => relayActorName(e.changedBy ?? e.by ?? e.actor ?? e.adminUserId ?? e.adminEmail);
const relayEntryAt = (e) => e.changedAt ?? e.at ?? e.timestamp ?? e.createdAt;
const relayEntryAction = (e) => e.action ?? e.status ?? e.event ?? e.to ?? '';

function relayChangeLine(status, relay) {
    if (!relay) return null;
    const history = Array.isArray(relay.history) ? relay.history : [];
    const last = history.length ? history[history.length - 1] : null;
    const by = relayActorName(relay.changedBy) || (last && relayEntryBy(last)) || 'unknown';
    const at = relayDate(relay.changedAt || (last && relayEntryAt(last))) || 'an unknown date';
    const reason = relay.reason ?? (last ? last.reason : null);
    if (status === 'disabled') return `Turned off by ${by} on ${at}${reason ? `: ${reason}` : ''}`;
    if (status === 'enabled') return `Turned on by ${by} on ${at}`;
    if (status === 'denied') return `Denied by ${by} on ${at}${reason ? `: ${reason}` : ''}`;
    return null;
}

const relayErrorLine = (err, fallback) => err.response?.data?.detail
    || err.response?.data?.message
    || err.response?.data?.error
    || err.message
    || fallback;

// The relay agent within the agents payload, matched across likely id/type fields.
// Used only to keep it out of the toggle list: the Relay access card is its one control.
const isRelayAgent = (a) => {
    const k = String(agentKey(a) ?? '').toLowerCase();
    const t = String(a.type ?? a.agentType ?? '').toLowerCase();
    return k === 'relay_agent' || k === 'relay' || t === 'relay' || t === 'relay_agent';
};

// The icon system prints iconHint directly, expecting an emoji glyph. Some agents
// (e.g. Healthcare) carry a text label instead ("stethoscope"), which would render
// as raw text. Map those word-keys to the matching emoji; emoji hints pass through
// unchanged, and a missing hint falls back to the default robot.
const ICON_HINT_MAP = {
    stethoscope: '🩺',
};
const resolveAgentIcon = (hint) => {
    if (!hint) return '🤖';
    return ICON_HINT_MAP[String(hint).toLowerCase()] || hint;
};

// Exported so sibling enterprise tabs reuse the exact Firebase-bearer idiom.
export async function authHeader() {
    const user = getAuth().currentUser;
    if (!user) throw new Error('Not authenticated. Please sign in again.');
    const token = await user.getIdToken();
    return { Authorization: `Bearer ${token}` };
}

// -----------------------------------------------------------------------------
//  COMPONENT
// -----------------------------------------------------------------------------

export default function EnterpriseAgentAccessTab({ orgId, orgName }) {
    const [agents, setAgents] = useState(null);   // null = not yet loaded
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState({});      // { [agentKey]: true }
    const [toast, setToast] = useState({ open: false, severity: 'success', message: '' });

    // ---- Relay access (T7) ----
    const [relay, setRelay] = useState(null); // null = not yet loaded; else { status, changedBy, changedAt, reason, history }
    const [relayError, setRelayError] = useState(null);
    const [relaySaving, setRelaySaving] = useState(false);
    const [relayOverrideStatus, setRelayOverrideStatus] = useState(null); // optimistic status until reconciled
    const [disableOpen, setDisableOpen] = useState(false);
    const [disableReason, setDisableReason] = useState('');
    const [historyOpen, setHistoryOpen] = useState(false);

    const showToast = (severity, message) => setToast({ open: true, severity, message });

    // ---------------- fetch ----------------
    const fetchAgents = useCallback(async () => {
        if (!orgId) {
            setError('No organization selected. Open this page from the Enterprises list.');
            setAgents(null);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const headers = await authHeader();
            const res = await axios.get(`${T6_BASE}/api/admin/orgs/${orgId}/agents`, { headers });
            const list = Array.isArray(res.data) ? res.data : (res.data?.agents ?? []);
            setAgents(list);
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Failed to load agents.');
            setAgents(null);
        } finally {
            setLoading(false);
        }
    }, [orgId]);

    useEffect(() => {
        fetchAgents();
    }, [fetchAgents]);

    // Relay: one status read for THIS org. The card shows only what this answer says.
    const fetchRelay = useCallback(async () => {
        if (!orgId) return;
        setRelayError(null);
        try {
            const headers = await authHeader();
            const res = await axios.get(`${T6_BASE}/api/admin/relay/orgs/${orgId}`, { headers });
            const d = res.data || {};
            if (typeof d.status !== 'string') throw new Error('Relay status missing from the server answer.');
            setRelay({ ...d, history: Array.isArray(d.history) ? d.history : [] });
        } catch (err) {
            setRelayError(relayErrorLine(err, 'Failed to load relay status.'));
            setRelay(null);
        }
    }, [orgId]);

    useEffect(() => {
        fetchRelay();
    }, [fetchRelay]);

    // ---------------- mutations ----------------

    // Shared PUT routine. `enabled` is the desired flag; on success we replace
    // the card with whatever the server reports (effectiveEnabled, orgOverride).
    const putAgent = useCallback(async (agent, enabled, successMsg) => {
        const key = agentKey(agent);
        const prev = agents;
        // optimistic local update of the switch
        setAgents((cur) => cur.map((a) =>
            agentKey(a) === key ? { ...a, effectiveEnabled: enabled } : a
        ));
        setSaving((s) => ({ ...s, [key]: true }));
        try {
            const headers = await authHeader();
            const res = await axios.put(
                `${T6_BASE}/api/admin/orgs/${orgId}/agents/${key}`,
                { enabled, reason: '' },
                { headers }
            );
            const updated = res.data?.agent ?? res.data;
            setAgents((cur) => cur.map((a) =>
                agentKey(a) === key ? { ...a, ...updated } : a
            ));
            if (successMsg) showToast('success', successMsg);
        } catch (err) {
            setAgents(prev); // rollback
            showToast('error', err.response?.data?.message || err.message || 'Update failed.');
        } finally {
            setSaving((s) => {
                const next = { ...s };
                delete next[key];
                return next;
            });
        }
    }, [agents, orgId]);

    const handleToggle = (agent) => {
        const next = !agent.effectiveEnabled;
        putAgent(agent, next, `${agent.displayName}: ${next ? 'enabled' : 'disabled'}.`);
    };

    // Reset clears the per-org override so the agent follows the platform default.
    // FIXME[T6/T1]: no real "clear override" endpoint yet. MVP hack: PUT the
    // default value, which sets an override matching the default rather than
    // truly clearing it. Needs a proper DELETE/clear-override action backend-side.
    const handleReset = (agent) => {
        putAgent(agent, agent.defaultEnabled, `${agent.displayName}: reset to default.`);
    };

    // Relay mutations. Mirror putAgent + GenesisPage.handleApprove: optimistic chip,
    // POST with Bearer authHeader(), reconcile from the answer, then refetch the org's
    // status and history. On error revert and surface the server's error line
    // (Rule 3, no silent success).
    const runRelayChange = useCallback(async ({ action, reason, optimistic, okMsg, failMsg }) => {
        const prevOverride = relayOverrideStatus;
        setRelayOverrideStatus(optimistic);
        setRelaySaving(true);
        try {
            const headers = await authHeader();
            const res = await axios.post(
                `${T6_BASE}/api/admin/relay/requests/${orgId}/${action}`,
                { reason },
                { headers }
            );
            const answered = res.data?.status ? normalizeRelayStatus(res.data.status) : optimistic;
            setRelay((cur) => ({ ...(cur || { history: [] }), status: answered }));
            setRelayOverrideStatus(null);
            showToast('success', okMsg);
            fetchRelay();
        } catch (err) {
            setRelayOverrideStatus(prevOverride); // revert
            showToast('error', relayErrorLine(err, failMsg));
        } finally {
            setRelaySaving(false);
        }
    }, [orgId, relayOverrideStatus, fetchRelay]);

    // Approve (requested), Enable (not requested), Re-enable (disabled): all the approve route.
    const handleRelayApprove = (verb) => runRelayChange({
        action: 'approve',
        reason: verb === 'Re-enable' ? 're_enabled_from_admin' : 'approved_from_admin',
        optimistic: 'enabled',
        okMsg: 'Relay enabled for this org.',
        failMsg: 'Relay approve failed.',
    });

    // Disable: only from the confirm dialog, only with a reason.
    const handleRelayDisable = () => {
        const reason = disableReason.trim();
        if (!reason) return;
        setDisableOpen(false);
        setDisableReason('');
        runRelayChange({
            action: 'disable',
            reason,
            optimistic: 'disabled',
            okMsg: 'Relay turned off for this org.',
            failMsg: 'Relay disable failed.',
        });
    };

    // ---------------- render: body states ----------------

    const renderBody = () => {
        if (loading) {
            return [0, 1, 2].map((i) => (
                <AgentCard key={i}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Skeleton variant="circular" width={40} height={40} sx={{ bgcolor: '#222' }} />
                        <Box sx={{ flex: 1 }}>
                            <Skeleton variant="text" width="40%" sx={{ bgcolor: '#222' }} />
                            <Skeleton variant="text" width="60%" sx={{ bgcolor: '#1a1a1a' }} />
                        </Box>
                        <Skeleton variant="rounded" width={52} height={32} sx={{ bgcolor: '#222' }} />
                    </Box>
                </AgentCard>
            ));
        }

        if (error) {
            return (
                <Alert
                    severity="error"
                    sx={{ bgcolor: 'rgba(255,107,107,0.1)', color: '#ff6b6b', border: '1px solid rgba(255,107,107,0.3)' }}
                    action={
                        <Button color="inherit" size="small" onClick={fetchAgents}>
                            Retry
                        </Button>
                    }
                >
                    {error}
                </Alert>
            );
        }

        if (!agents) return null;

        // Relay renders as the dedicated "Relay access" card at the top, so drop it
        // from the generic per-agent toggle list: that card is Relay's one control.
        // Every OTHER agent renders unchanged.
        const otherAgents = agents.filter((a) => !isRelayAgent(a));

        if (otherAgents.length === 0) {
            return (
                <Box sx={{ textAlign: 'center', py: 6, color: '#666' }}>
                    <Typography sx={{ fontStyle: 'italic' }}>
                        No agents available for this org.
                    </Typography>
                </Box>
            );
        }

        return otherAgents.map((agent) => {
            const key = agentKey(agent);
            const pill = statusPill(agent);
            const isSaving = !!saving[key];
            const hasOverride = agent.orgOverride === 'enabled' || agent.orgOverride === 'disabled';

            return (
                <AgentCard key={key}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        {/* Icon */}
                        <Box sx={{ fontSize: '2rem', lineHeight: 1, mt: 0.25 }}>
                            {resolveAgentIcon(agent.iconHint)}
                        </Box>

                        {/* Title + tagline */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <Typography sx={{ fontWeight: 700, fontSize: '1.05rem' }}>
                                    {agent.displayName}
                                </Typography>
                                {agent.description && (
                                    <Tooltip
                                        title={agent.description}
                                        arrow
                                        placement="top"
                                    >
                                        <InfoOutlinedIcon
                                            sx={{ fontSize: '1rem', color: '#888', cursor: 'help' }}
                                        />
                                    </Tooltip>
                                )}
                            </Box>
                            {agent.tagline && (
                                <Typography sx={{ color: '#888', fontSize: '0.85rem', mt: 0.25 }}>
                                    {agent.tagline}
                                </Typography>
                            )}
                        </Box>

                        {/* Switch + status, right-aligned */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5, flexShrink: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: 38 }}>
                                {isSaving && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <CircularProgress size={14} sx={{ color: '#12c099' }} />
                                        <Typography sx={{ color: '#888', fontSize: '0.75rem' }}>Saving…</Typography>
                                    </Box>
                                )}
                                <Switch
                                    size="medium"
                                    checked={!!agent.effectiveEnabled}
                                    disabled={isSaving}
                                    onChange={() => handleToggle(agent)}
                                    sx={{
                                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#12c099' },
                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#12c099' }
                                    }}
                                />
                            </Box>

                            <Chip
                                label={pill.label}
                                size="small"
                                sx={{
                                    backgroundColor: pill.bg,
                                    color: pill.color,
                                    fontWeight: 600,
                                    fontSize: '0.72rem',
                                    height: 22
                                }}
                            />

                            {hasOverride && (
                                <MuiLink
                                    component="button"
                                    type="button"
                                    onClick={() => handleReset(agent)}
                                    disabled={isSaving}
                                    sx={mintLinkSx}
                                >
                                    Reset to default
                                </MuiLink>
                            )}
                        </Box>
                    </Box>
                </AgentCard>
            );
        });
    };

    // ---------------- relay status (computed) ----------------
    // One status, from the org read. An optimistic override wins until reconciled/
    // reverted. "Ready" gates the initial "Checking" state.
    const relayStatus = relayOverrideStatus || normalizeRelayStatus(relay?.status);
    const relayReady = relay !== null;
    const relayPillDesc = relayPillFor(relayStatus);
    const relayLine = relayOverrideStatus ? null : relayChangeLine(relayStatus, relay);
    const relayHistory = Array.isArray(relay?.history) ? relay.history : [];
    const relayActionLabel = { requested: 'Approve', not_requested: 'Enable', disabled: 'Re-enable' }[relayStatus];
    const companyName = orgName || 'this company';

    // ---------------- render ----------------

    return (
        <Box>
            {/* HEADER */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        Agent Access
                    </Typography>
                    <Typography sx={{ color: '#888', fontSize: '0.9rem', mt: 0.5, maxWidth: 620 }}>
                        Enable or disable agents for this org. Toggles take effect immediately for new requests.
                    </Typography>
                </Box>

                <Button
                    variant="outlined"
                    startIcon={<RefreshOutlinedIcon />}
                    onClick={fetchAgents}
                    disabled={loading}
                    sx={{
                        borderColor: '#333',
                        color: '#fff',
                        textTransform: 'none',
                        flexShrink: 0,
                        '&:hover': { borderColor: '#12c099', color: '#12c099' }
                    }}
                >
                    Refresh
                </Button>
            </Box>

            {/* RELAY ACCESS */}
            <AgentCard>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: '1.05rem' }}>Relay access</Typography>
                        <Typography sx={{ color: '#888', fontSize: '0.85rem', mt: 0.25 }}>
                            Whether the Relay agent is enabled for this org.
                        </Typography>
                        {relayReady && !relayError && relayLine && (
                            <Typography data-testid="relay-line" sx={{ color: '#aaa', fontSize: '0.8rem', mt: 0.5 }}>
                                {relayLine}
                            </Typography>
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
                        {relayError ? (
                            <>
                                <Typography sx={{ color: '#ff6b6b', fontSize: '0.8rem' }}>{relayError}</Typography>
                                <Button size="small" onClick={fetchRelay} sx={{ color: '#12c099', textTransform: 'none' }}>
                                    Retry
                                </Button>
                            </>
                        ) : !relayReady ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <CircularProgress size={14} sx={{ color: '#12c099' }} />
                                <Typography sx={{ color: '#888', fontSize: '0.8rem' }}>Checking…</Typography>
                            </Box>
                        ) : (
                            <>
                                <Chip
                                    data-testid="relay-chip"
                                    label={relayPillDesc.label}
                                    size="small"
                                    sx={{
                                        backgroundColor: relayPillDesc.bg,
                                        color: relayPillDesc.color,
                                        fontWeight: 600,
                                        fontSize: '0.72rem',
                                        height: 22
                                    }}
                                />
                                {relayActionLabel && (
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={() => handleRelayApprove(relayActionLabel)}
                                        disabled={relaySaving}
                                        startIcon={relaySaving ? <CircularProgress size={14} sx={{ color: '#06281f' }} /> : null}
                                        sx={{
                                            backgroundColor: '#12c099',
                                            color: '#06281f',
                                            textTransform: 'none',
                                            fontWeight: 700,
                                            '&:hover': { backgroundColor: '#0fa884' }
                                        }}
                                    >
                                        {relaySaving ? 'Saving…' : relayActionLabel}
                                    </Button>
                                )}
                                {relayStatus === 'enabled' && (
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => setDisableOpen(true)}
                                        disabled={relaySaving}
                                        startIcon={relaySaving ? <CircularProgress size={14} sx={{ color: '#ff6b6b' }} /> : null}
                                        sx={{
                                            borderColor: 'rgba(255,107,107,0.5)',
                                            color: '#ff6b6b',
                                            textTransform: 'none',
                                            fontWeight: 700,
                                            '&:hover': { borderColor: '#ff6b6b', backgroundColor: 'rgba(255,107,107,0.1)' }
                                        }}
                                    >
                                        {relaySaving ? 'Saving…' : 'Disable'}
                                    </Button>
                                )}
                            </>
                        )}
                    </Box>
                </Box>

                {relayReady && !relayError && (
                    <Box sx={{ mt: 1 }}>
                        <MuiLink component="button" type="button" onClick={() => setHistoryOpen((o) => !o)} sx={mintLinkSx}>
                            {historyOpen ? 'Hide history' : 'History'}
                        </MuiLink>
                        <Collapse in={historyOpen}>
                            <Box data-testid="relay-history" sx={{ mt: 1, borderTop: '1px solid rgba(255,255,255,0.08)', pt: 1 }}>
                                {relayHistory.length === 0 ? (
                                    <Typography sx={{ color: '#666', fontSize: '0.8rem', fontStyle: 'italic' }}>
                                        No Relay changes recorded.
                                    </Typography>
                                ) : [...relayHistory].reverse().map((e, i) => (
                                    <Typography key={i} sx={{ color: '#aaa', fontSize: '0.8rem', py: 0.25 }}>
                                        {relayDate(relayEntryAt(e)) || '—'} · {relayEntryAction(e) || 'change'} · {relayEntryBy(e) || 'unknown'}
                                        {e.reason ? ` · ${e.reason}` : ''}
                                    </Typography>
                                ))}
                            </Box>
                        </Collapse>
                    </Box>
                )}
            </AgentCard>

            <Dialog
                open={disableOpen}
                onClose={() => setDisableOpen(false)}
                fullWidth
                maxWidth="sm"
                PaperProps={{ sx: { bgcolor: '#111', color: '#fff', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '14px' } }}
            >
                <DialogTitle sx={{ fontWeight: 700 }}>Turn off Relay</DialogTitle>
                <DialogContent>
                    <Typography sx={{ color: '#ccc', fontSize: '0.9rem', mb: 2 }}>
                        Turn off Relay for {companyName}? Their team loses Relay right away and queued Relay messages are cancelled.
                    </Typography>
                    <TextField
                        autoFocus
                        fullWidth
                        required
                        multiline
                        minRows={2}
                        label="Reason"
                        value={disableReason}
                        onChange={(e) => setDisableReason(e.target.value)}
                        inputProps={{ 'data-testid': 'relay-disable-reason' }}
                        InputLabelProps={{ sx: { color: '#888', '&.Mui-focused': { color: '#ff6b6b' } } }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                color: '#fff',
                                '& fieldset': { borderColor: '#333' },
                                '&.Mui-focused fieldset': { borderColor: '#ff6b6b' }
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDisableOpen(false)} sx={{ color: '#888', textTransform: 'none' }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleRelayDisable}
                        disabled={!disableReason.trim()}
                        sx={{
                            backgroundColor: '#ff6b6b',
                            color: '#2a0a0a',
                            textTransform: 'none',
                            fontWeight: 700,
                            '&:hover': { backgroundColor: '#e85d5d' }
                        }}
                    >
                        Turn off Relay
                    </Button>
                </DialogActions>
            </Dialog>

            {/* BODY */}
            {renderBody()}

            <Snackbar
                open={toast.open}
                autoHideDuration={3500}
                onClose={() => setToast({ ...toast, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    severity={toast.severity}
                    onClose={() => setToast({ ...toast, open: false })}
                    sx={{ width: '100%' }}
                >
                    {toast.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
